import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ExternalLink, Video, Quote } from "lucide-react";
import { submitInterviewRequest } from "@/lib/interview-requests.functions";
import { requestInterviewGuide } from "@/lib/guide-signups.functions";


const SITE_URL = "https://mycollegeinterview.com";
const BRAND = "My College Interview";
const PAGE_TITLE = "College Interview Prep — Practice Interviews With Real Feedback";
const PAGE_DESCRIPTION =
  "Friendly 1:1 practice college interviews for juniors and rising seniors. Real questions, calm coaching, and written feedback from a 10+ year higher-ed admissions interviewer.";
const SOCIAL_TITLE = "College Interview Prep — Practice Interviews With Real Feedback";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESCRIPTION },
      { property: "og:title", content: SOCIAL_TITLE },
      { property: "og:description", content: PAGE_DESCRIPTION },
      { property: "og:url", content: SITE_URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:title", content: SOCIAL_TITLE },
      { name: "twitter:description", content: PAGE_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],

    links: [{ rel: "canonical", href: SITE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "College Interview Practice Sessions",
          serviceType: "College admission interview coaching",
          provider: { "@type": "Organization", name: BRAND, url: SITE_URL },
          areaServed: "Worldwide",
          audience: { "@type": "EducationalAudience", educationalRole: "student" },
          description: PAGE_DESCRIPTION,
          offers: [
            {
              "@type": "Offer",
              name: "The Warm-Up",
              description:
                "One 30-minute general practice college interview with written feedback. Not school specific.",
              price: "55",
              priceCurrency: "USD",
              url: SITE_URL,
            },
            {
              "@type": "Offer",
              name: "The School Fit",
              description:
                "One 45-minute practice interview built around a specific college, with written feedback.",
              price: "99",
              priceCurrency: "USD",
              url: SITE_URL,
            },
            {
              "@type": "Offer",
              name: "The Unlimited Pass",
              description:
                "Unlimited 45-minute school-specific practice interviews through your application season.",
              price: "399",
              priceCurrency: "USD",
              url: SITE_URL,
            },
          ],
        }),
      },
    ],
  }),
});

type PackageId = "practice-30" | "school-45" | "unlimited-45";

const PACKAGES: {
  id: PackageId;
  name: string;
  price: string;
  duration: string;
  tagline: string;
  perks: string[];
  schoolSpecific: boolean;
  featured?: boolean;
}[] = [
  {
    id: "practice-30",
    name: "The Warm-Up",
    price: "$55",
    duration: "30 min",
    tagline: "A guided first run - when you just want to practice without prepping for a specific school.",
    schoolSpecific: false,
    perks: [
      "One 30-minute practice interview",
      "Common college interview questions",
      "Written feedback within 24 hours",
      "Great for first-time interviewers",
    ],
  },
  {
    id: "school-45",
    name: "The School Fit",
    price: "$99",
    duration: "45 min",
    tagline: "An extended practice interview for the college you're actually applying to.",
    schoolSpecific: true,
    featured: true,
    perks: [
      "One 45-minute practice interview",
      "Questions tailored to your school",
      '"Why us?" and fit answers workshopped',
      "Written feedback within 24 hours",
    ],
  },
  {
    id: "unlimited-45",
    name: "The Unlimited Pass",
    price: "$399",
    duration: "Unlimited 45-min sessions",
    tagline: "Keep practicing all season, as many schools as you like.",
    schoolSpecific: true,
    perks: [
      "Unlimited 45-minute sessions",
      "A different school each time, if you want",
      "Fresh questions every round",
      "Written feedback after every session",
    ],
  },
];

const TESTIMONIALS: { quote: string; name: string; detail: string }[] = [
  {
    quote: "I learned how to think and answer quickly with an answer that is authentic to me.",
    name: "Avery",
    detail: "CLASS OF 2024 · BOSTON COLLEGE",
  },
  {
    quote: "It really got me to put my skills to the test and then helped me understand what I needed to improve on.",
    name: "Elliott",
    detail: "CLASS OF 2023 · WASHINGTON UNIVERSITY",
  },
  {
    quote: "I've partnered with Chris for years and he's helped all of my students sharpen their interviewing skills.",
    name: "East-Coast Based",
    detail: "INDEPENDENT COUNSELOR",
  },
  {
    quote: "You explained why I most likely made the mistakes I did, which makes it much easier to rectify them in future.",
    name: "Sophie",
    detail: "CLASS OF 2024 · CALTECH",
  },
  {
    quote: "I didn't feel nervous when I went to interview for real. I felt prepared.",
    name: "Mona",
    detail: "CLASS OF 2025 · GEORGETOWN",
  },
  {
    quote: "Your calm demeanor was helpful in reducing stress around interviews!",
    name: "Adam",
    detail: "CLASS OF 2025 · UC BERKELEY",
  },
];

function Index() {
  const [selected, setSelected] = useState<PackageId>("school-45");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const selectedPackage = PACKAGES.find((p) => p.id === selected)!;
  const showSchoolField = selectedPackage.schoolSpecific;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);

    const formData = new FormData(e.currentTarget);

    try {
      await submitInterviewRequest({
        data: {
          packageId: selected,
          packageName: selectedPackage.name,
          packagePrice: selectedPackage.price,
          name: String(formData.get("name") ?? ""),
          email: String(formData.get("email") ?? ""),
          phone: String(formData.get("phone") ?? ""),
          school: showSchoolField ? String(formData.get("school") ?? "") : "",
          times: String(formData.get("times") ?? ""),
          notes: String(formData.get("notes") ?? ""),
        },
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Submission failed:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:font-semibold focus:text-primary-foreground"
      >
        Skip to main content
      </a>

      {/* NAV */}
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-6 py-6">
        <a href="#top" className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-md gradient-crimson font-display text-lg text-primary-foreground">
            C
          </span>
          <span className="font-display text-lg leading-tight tracking-tight">
            My College <span className="text-primary">Interview</span>
          </span>
        </a>
        <nav className="hidden items-center gap-8 text-sm font-medium text-muted-foreground md:flex">
          <a href="#packages" className="flex min-h-11 items-center hover:text-foreground">Sessions</a>
          <a href="#how" className="flex min-h-11 items-center hover:text-foreground">How it works</a>
          <a href="#testimonials" className="flex min-h-11 items-center hover:text-foreground">Testimonials</a>
          <a href="#book" className="flex min-h-11 items-center hover:text-foreground">Book</a>
          <a href="#about" className="flex min-h-11 items-center hover:text-foreground">About</a>
        </nav>
      </header>

      <main id="main">
        {/* HERO */}
        <section id="top" className="relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-70" aria-hidden />
          <div
            className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full opacity-60 blur-3xl"
            style={{ background: "var(--accent)" }}
            aria-hidden
          />
          <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-14 md:pt-20">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-3 py-1 text-xs uppercase tracking-widest text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Now booking juniors &amp; rising seniors
            </div>
            <h1 className="font-display mt-6 text-balance text-4xl md:text-6xl">
              Practice for your&nbsp;
              <br />
              college interviews
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">
              Live 1:1 practice college interviews where you'll get real questions, coaching during the call, and a written debrief you can actually use.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-card px-3 py-1 text-xs font-medium text-primary">
              <Video className="h-3.5 w-3.5" />
              Virtual session over Google Meet
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#book"
                className="flex min-h-12 min-w-12 items-center justify-center rounded-md bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-crimson transition hover:brightness-110"
              >
                Book a practice interview →
              </a>
              <a
                href="#packages"
                className="flex min-h-12 min-w-12 items-center justify-center rounded-md border border-border bg-card px-6 py-3 text-base font-semibold text-foreground transition hover:bg-secondary"
              >
                See the three options
              </a>
            </div>

            <dl className="mt-16 grid w-full grid-cols-3 gap-6 border-t border-border pt-8 text-center">
              {[
                { k: "10+", v: "Years in higher education" },
                { k: "1,000+", v: "Students interviewed" },
                { k: "24h", v: "TURNAROUND FOR FEEDBACK" },
              ].map((s) => (
                <div key={s.v}>
                  <dt className="font-display text-4xl text-foreground md:text-5xl">{s.k}</dt>
                  <dd className="mt-2 text-xs uppercase tracking-widest text-muted-foreground">
                    {s.v}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* PACKAGES */}
        <section id="packages" className="border-t border-border bg-card/60">
          <div className="mx-auto max-w-7xl px-6 py-24">
            <div>
              <p className="text-xs uppercase tracking-widest text-primary">01 — Sessions</p>
              <h2 className="font-display mt-3 text-4xl md:text-5xl">
                Three simple ways to practice.
              </h2>
              <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
                Every session is a real 1:1 conversation, not a quiz. You'll get written feedback
                afterward, and if a session isn't helpful, it's fully refunded — no awkward
                conversation required.
              </p>
            </div>

            <div className="mt-14 grid items-stretch gap-6 md:grid-cols-3">
              {PACKAGES.map((p) => {
                const isSelected = selected === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={isSelected}
                    aria-label={`Select ${p.name}, ${p.price}, ${p.duration}`}
                    onClick={() => {
                      setSelected(p.id);
                      document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className={[
                      "group relative flex h-full flex-col rounded-xl border bg-card p-8 text-left transition",
                      isSelected
                        ? "border-primary shadow-crimson"
                        : "border-border hover:border-primary/40 hover:shadow-soft",
                    ].join(" ")}
                  >
                    {p.featured && (
                      <span className="absolute -top-3 left-8 rounded-full bg-accent px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-accent-foreground">
                        Most students pick this
                      </span>
                    )}
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      {p.duration}
                    </div>
                    <div className="font-display mt-3 text-3xl">{p.name}</div>
                    <div className="font-display mt-5 text-5xl text-primary">{p.price}</div>
                    <p className="mt-3 text-sm text-muted-foreground">{p.tagline}</p>
                    <ul className="mt-6 space-y-2 text-sm">
                      {p.perks.map((perk) => (
                        <li key={perk} className="flex gap-2">
                          <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                          <span>{perk}</span>
                        </li>
                      ))}
                    </ul>
                    <div
                      className={[
                        "mt-8 inline-flex items-center gap-2 pt-2 text-sm font-semibold",
                        isSelected ? "text-primary" : "text-foreground",
                      ].join(" ")}
                    >
                      {isSelected ? "Selected" : "Choose this →"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how" className="border-t border-border">
          <div className="mx-auto max-w-7xl px-6 py-24">
            <p className="text-xs uppercase tracking-widest text-primary">02 — How it works</p>
            <h2 className="font-display mt-3 text-4xl md:text-5xl">
              Just three simple steps
            </h2>

            <ol className="mt-14 grid gap-8 md:grid-cols-3">
              {[
                {
                  n: "01",
                  t: "Pick a session",
                  d: "Choose one of the three options and send over your contact info and a few times that work.",
                },
                {
                  n: "02",
                  t: "Practice your interview",
                  d: "We meet on Google Meet. I ask, you answer, and we talk through it together near the end.",
                },
                {
                  n: "03",
                  t: "Review your debrief",
                  d: "A written, encouraging summary of what landed and what to tighten arrives within 24 hours.",
                },
              ].map((s) => (
                <li key={s.n} className="rounded-xl border border-border bg-card p-6 shadow-soft">
                  <div className="font-display text-3xl text-primary">{s.n}</div>
                  <div className="mt-3 font-display text-2xl">{s.t}</div>
                  <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="testimonials" className="border-t border-border bg-accent/40">
          <div className="mx-auto max-w-7xl px-6 py-24">
            <p className="text-xs uppercase tracking-widest text-primary">03 — TESTIMONIALS</p>
            <h2 className="font-display mt-3 text-4xl md:text-5xl">In their words.</h2>

            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {TESTIMONIALS.map((t, i) => (
                <figure
                  key={i}
                  className="flex h-full flex-col rounded-xl border border-border bg-card p-7 shadow-soft"
                >
                  <Quote className="h-6 w-6 text-primary/50" aria-hidden />
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-6 border-t border-border pt-4">
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      {t.detail}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* BOOKING FORM */}
        <section id="book" className="border-t border-border">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-16 px-6 py-24 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-primary">04 — Book</p>
              <h2 className="font-display mt-3 text-4xl text-balance md:text-5xl">
                Let's find a time.
              </h2>
              <p className="mt-6 text-muted-foreground">
                Fill this out, and I'll email you back within a day to lock in a session over Google
                Meet. I suggest booking at least a week before a student's real interview. I host a
                limited number of sessions each week, so earlier is easier — especially in the fall.
                Parents are welcome to book on a student's behalf!
              </p>
              <div className="mt-10 rounded-xl border border-border bg-card p-6 shadow-soft">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  Currently selected
                </div>
                <div className="font-display mt-2 text-3xl">{selectedPackage.name}</div>
                <div className="mt-1 text-primary">
                  {selectedPackage.price} · {selectedPackage.duration}
                </div>
              </div>
            </div>

            {submitted ? (
              <div
                ref={successRef}
                tabIndex={-1}
                role="status"
                aria-live="polite"
                className="flex flex-col justify-center rounded-xl border border-primary bg-card p-10 shadow-crimson"
              >
                <div className="font-display text-4xl text-primary">You're all set.</div>
                <p className="mt-4 text-muted-foreground">
                  I've got your request. Look for an email from me within 24 hours to confirm a
                  time. Nothing to prepare in the meantime — just show up as yourself.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-8 flex min-h-11 items-center self-start text-sm font-semibold text-foreground underline underline-offset-4"
                >
                  Submit another
                </button>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-card p-8 shadow-soft md:p-10">
                <form onSubmit={handleSubmit}>
                  <div className="grid gap-5">
                    <div className="grid gap-2">
                      <label
                        htmlFor="package"
                        className="text-xs uppercase tracking-widest text-muted-foreground"
                      >
                        Session
                      </label>
                      <select
                        id="package"
                        value={selected}
                        onChange={(e) => setSelected(e.target.value as PackageId)}
                        className="w-full rounded-md border border-border bg-input px-4 py-3 font-medium text-foreground focus:border-primary"
                        required
                      >
                        {PACKAGES.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} — {p.price} ({p.duration})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field id="name" label="Student name" name="name" required placeholder="Jordan Lee" />
                      <Field
                        id="email"
                        label="Email"
                        name="email"
                        type="email"
                        required
                        placeholder="jordan@example.com"
                      />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field id="phone" label="Phone" name="phone" type="tel" placeholder="+1 555 000 1234" />
                      {showSchoolField && (
                        <Field
                          id="school"
                          label="The school you're targeting"
                          name="school"
                          placeholder="e.g. Chapman University"
                        />
                      )}
                    </div>

                    <div className="grid gap-2">
                      <label
                        htmlFor="times"
                        className="text-xs uppercase tracking-widest text-muted-foreground"
                      >
                        Preferred times and days (w/ time zone)
                      </label>
                      <input
                        id="times"
                        name="times"
                        placeholder="e.g. weekday evenings PST, or Tue/Thu after school"
                        className="w-full rounded-md border border-border bg-input px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary"
                      />
                    </div>

                    <div className="grid gap-2">
                      <label
                        htmlFor="notes"
                        className="text-xs uppercase tracking-widest text-muted-foreground"
                      >
                        Anything else you'd like me to know?{" "}
                        <span className="text-muted-foreground">(optional)</span>
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        rows={5}
                        maxLength={8000}
                        placeholder="Nerves, a specific question you're dreading, intended major, activities you want to talk about — anything helps."
                        className="w-full resize-y rounded-md border border-border bg-input px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="mt-2 flex min-h-12 w-full items-center justify-center rounded-md bg-primary px-6 py-4 font-semibold text-primary-foreground shadow-crimson transition hover:brightness-110 disabled:opacity-60 sm:w-auto"
                    >
                      {submitting ? "Sending…" : "Request my practice interview →"}
                    </button>
                    <p className="text-xs text-muted-foreground">
                      No payment now — a payment link comes in the confirmation email once we've
                      picked a time.
                    </p>
                  </div>
                </form>

                <div className="mt-5">
                  <GuideCapture />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ABOUT */}
        <section id="about" className="border-t border-border bg-card/60">
          <div className="mx-auto grid max-w-7xl items-start gap-12 px-6 py-24 md:grid-cols-[380px_1fr]">
            <div className="relative rounded-xl">
              <img
                src="/chris-helvajian-crimson.png"
                alt="Chris Helvajian, higher education admissions interviewer and founder of My College Interview"
                loading="lazy"
                width={1024}
                height={1024}
                className="w-full rounded-lg object-cover shadow-soft"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-primary">05 — About me</p>
              <h2 className="font-display mt-3 text-4xl md:text-5xl">Hi, I'm Chris.</h2>
              <p className="mt-6 text-muted-foreground">
                I spent 10+ years working in higher education, including at UC Irvine and Chapman
                University. I've interviewed, evaluated, and advised thousands of students — which
                means I know what selective institutions are listening to and looking for in
                college interviews.
              </p>
              <p className="mt-4 text-muted-foreground">
                Practicing with me is a low-stakes way to work on telling your story clearly,
                answering "why us?" like you mean it, and handling the questions you can't predict.
                My job isn't to judge you — it's to help you be the most prepared, most genuinely
                you version of yourself during your interview.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 p-4 shadow-[0_-8px_30px_rgba(30,30,30,0.12)] backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              Book a practice interview
            </p>
            <p className="truncate text-xs text-muted-foreground">
              From {PACKAGES[0].price} · Virtual session
            </p>
          </div>
          <a
            href="#book"
            className="flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-crimson transition hover:brightness-110"
          >
            Book now
          </a>
        </div>
      </div>

      {/* PREFERRED SOURCES */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center">
          <p className="text-xs uppercase tracking-widest text-primary">Follow on Google</p>
          <h2 className="font-display mt-3 text-2xl md:text-3xl">
            Get college interview tips in your Google feed
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            Add My College Interview as a preferred source so our latest guides and updates show up where you already read.
          </p>
          <div className="mt-6 flex justify-center">
            <a
              href="https://www.google.com/preferences/source?q=mycollegeinterview.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Add My College Interview as a preferred source on Google (opens in a new tab)"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-crimson transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Add on Google
              <ExternalLink aria-hidden="true" className="h-4 w-4" />
            </a>
          </div>

        </div>
      </section>

      <footer className="border-t border-border pb-20 md:pb-0">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 py-10 md:flex-row md:items-center">
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded gradient-crimson font-display text-sm text-primary-foreground">
              C
            </span>
            <span className="font-display">{BRAND}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {BRAND}
          </p>
        </div>
      </footer>
    </div>
  );
}

function GuideCapture() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const emailRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && state !== "done") emailRef.current?.focus();
  }, [open, state]);

  useEffect(() => {
    if (state === "done") doneRef.current?.focus();
  }, [state]);

  async function handleGuideSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState("sending");
    try {
      await requestInterviewGuide({ data: { email: email.trim() } });
      setState("done");
    } catch (err) {
      console.error("Guide signup failed:", err);
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <div
        ref={doneRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className="rounded-xl border border-primary/40 bg-accent/50 p-6"
      >
        <div className="font-display text-2xl text-primary">Guide sent.</div>
        <p className="mt-2 text-sm text-muted-foreground">
          Check your inbox — the free college interview guide is on its way to {email}.
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        className="flex min-h-12 w-full items-center justify-center rounded-md bg-foreground px-6 py-4 text-sm font-semibold text-background transition hover:opacity-90"
      >
        <span>
          Not ready to book?
          <br />
          Grab my free college interview guide
        </span>
      </button>
    );
  }

  return (
    <form onSubmit={handleGuideSubmit} className="rounded-xl border border-border bg-accent/40 p-6">
      <div className="font-display text-2xl">Free college interview guide</div>
      <p className="mt-2 text-sm text-muted-foreground">
        Drop your email and I'll send a one-page guide to your inbox — what to expect, what to
        prepare, and how to follow up.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label htmlFor="guide-email" className="sr-only">
          Email for the free college interview guide
        </label>
        <input
          ref={emailRef}
          id="guide-email"
          name="guide-email"
          type="email"
          required
          maxLength={255}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jordan@example.com"
          className="w-full flex-1 rounded-md border border-border bg-input px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className="flex min-h-12 w-full items-center justify-center rounded-md bg-primary px-6 py-3 font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-60 sm:w-auto"
        >
          {state === "sending" ? "Sending…" : "Send it →"}
        </button>
      </div>
      {state === "error" && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          Something went wrong. Please try again.
        </p>
      )}
    </form>
  );
}

function Field({
  id,
  label,
  name,
  type = "text",
  required,
  placeholder,
}: {
  id: string;
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div className="grid gap-2">
      <label htmlFor={id} className="text-xs uppercase tracking-widest text-muted-foreground">
        {label}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-md border border-border bg-input px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary"
      />
    </div>
  );
}
